import React, { useEffect, useState, useRef } from 'react';
import { cn } from '@/lib/utils';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeftIcon, CheckCircle2Icon, FingerprintIcon, ScanFaceIcon, EyeIcon, EyeOffIcon, ShieldIcon, SmartphoneIcon, MailIcon, UserIcon, LockIcon, CreditCardIcon, WalletIcon, BuildingIcon, GiftIcon, XCircleIcon, CheckCircleIcon, CameraIcon, TrophyIcon, ClockIcon, InfoIcon, KeyIcon, SparklesIcon, DeleteIcon } from 'lucide-react';
import { useTheme } from '../utils/theme';
import { RegistrationProgressBar } from './RegistrationProgressBar';
import { RegistrationCTAButton } from './RegistrationCTAButton';
type SignupStep = 'user_type' | 'terms' | 'details' | 'mobile_otp' | 'email_otp' | 'username' | 'password' | 'mpin' | 'bvs' | 'cooling_off';
type BvsPhase = 'right_guidance' | 'right_camera' | 'right_success' | 'left_guidance' | 'left_camera' | 'left_success';
interface BOPSignupFlowProps {
  onBack: () => void;
  onComplete: (userType?: 'bop_account' | 'bop_credit_card') => void;
}
// Mock taken usernames for availability check
const TAKEN_USERNAMES = ['admin', 'test', 'user', 'kashish', 'taken123', 'demo', 'bop', 'punjab'];
// Mock user data for username suggestions
const MOCK_USER = {
  firstName: 'Kashish',
  lastName: 'shewani',
  dob: '1995-04-12'
};
export function BOPSignupFlow({
  onBack,
  onComplete
}: BOPSignupFlowProps) {
  const {
    isDark,
    isPlatinum
  } = useTheme();
  const [step, setStep] = useState<SignupStep>('user_type');
  // Mobile Device Linking modal — shown after MPIN is confirmed, before BVS
  const [showLinkingModal, setShowLinkingModal] = useState(false);
  // Form State
  const [userType, setUserType] = useState('');
  const [termsLanguage, setTermsLanguage] = useState<'en' | 'ur'>('en');
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsScrolledToBottom, setTermsScrolledToBottom] = useState(false);
  const termsScrollRef = useRef<HTMLDivElement | null>(null);
  // Combined details
  const [cnic, setCnic] = useState('');
  const [accountNumber, setAccountNumber] = useState('');
  const [creditCardNumber, setCreditCardNumber] = useState('');
  const [creditCardExpiry, setCreditCardExpiry] = useState('');
  const [referralCode, setReferralCode] = useState('');
  const [showIbanInfo, setShowIbanInfo] = useState(false);
  const [mobileOtp, setMobileOtp] = useState(['', '', '', '', '', '']);
  const [mobileTimer, setMobileTimer] = useState(30);
  const [email, setEmail] = useState('');
  const [emailOtpSent, setEmailOtpSent] = useState(false);
  const [emailOtpTimer, setEmailOtpTimer] = useState(0);
  useEffect(() => {
    if (!emailOtpSent || emailOtpTimer <= 0) return;
    const id = setInterval(() => {
      setEmailOtpTimer(t => t > 0 ? t - 1 : 0);
    }, 1000);
    return () => clearInterval(id);
  }, [emailOtpSent, emailOtpTimer]);
  const [emailOtp, setEmailOtp] = useState(['', '', '', '', '', '']);
  // Username
  const [username, setUsername] = useState('');
  const [usernameTouched, setUsernameTouched] = useState(false);
  // Password
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  // MPIN — both fields visible at once like password screen
  const [mpin, setMpin] = useState(['', '', '', '', '', '']);
  const [confirmMpin, setConfirmMpin] = useState(['', '', '', '', '', '']);
  // BVS
  const [bvsPhase, setBvsPhase] = useState<BvsPhase>('right_guidance');
  const [bvsCameraTimer, setBvsCameraTimer] = useState(3);
  const [coolingOffTime, setCoolingOffTime] = useState(7200);
  // Username suggestions — refresh based on what the user is typing so the
  // suggestions feel relevant rather than static.
  const usernameSuggestions = (() => {
    const fn = MOCK_USER.firstName.toLowerCase();
    const ln = MOCK_USER.lastName.toLowerCase();
    const yr = MOCK_USER.dob.slice(0, 4);
    const day = MOCK_USER.dob.slice(8, 10);
    const seed = username.toLowerCase().replace(/\s/g, '');
    const base = seed.length >= 2 ? seed : `${fn}${ln}`;
    const candidates = seed.length >= 2 ? [`${base}${yr.slice(2)}`, `${base}_${day}`, `${base}.${ln}`, `${base}${Math.floor(Math.random() * 90 + 10)}`, `the${base}`] : [`${fn}${ln}${yr.slice(2)}`, `${fn}.${ln}${day}`, `${fn[0]}${ln}_${yr.slice(2)}${day}`, `${fn}_${ln}`, `${fn}${day}${yr.slice(2)}`];
    const unique = Array.from(new Set(candidates.filter(c => !TAKEN_USERNAMES.includes(c) && c !== seed)));
    return unique.slice(0, 3);
  })();
  // Username availability check (added "no space" rule)
  const usernameNoSpace = !/\s/.test(username);
  const isUsernameValid = username.length >= 6 && usernameNoSpace;
  const isUsernameTaken = TAKEN_USERNAMES.includes(username);
  const isUsernameAvailable = isUsernameValid && !isUsernameTaken;
  // Password validation
  const pwdLength = password.length >= 8;
  const pwdUpper = /[A-Z]/.test(password);
  const pwdLower = /[a-z]/.test(password);
  const pwdNum = /[0-9]/.test(password);
  const pwdSpec = /[!@#$%^&*]/.test(password);
  const pwdValidCount = [pwdLength, pwdUpper, pwdLower, pwdNum, pwdSpec].filter(Boolean).length;
  const pwdsMatch = password.length > 0 && password === confirmPassword;
  // Derived MPIN validators
  const mpinComplete = mpin.every(d => d !== '');
  const confirmMpinComplete = confirmMpin.every(d => d !== '');
  const mpinsMatch = mpin.join('') === confirmMpin.join('');
  // MPIN strength checks
  const mpinStr = mpin.join('');
  const mpinNonConsecutive = (() => {
    if (mpinStr.length < 6) return false;
    let ascending = true;
    let descending = true;
    for (let i = 1; i < mpinStr.length; i++) {
      const diff = Number(mpinStr[i]) - Number(mpinStr[i - 1]);
      if (diff !== 1) ascending = false;
      if (diff !== -1) descending = false;
    }
    return !(ascending || descending);
  })();
  const mpinNoTriples = (() => {
    if (mpinStr.length < 6) return false;
    for (let i = 0; i < mpinStr.length - 2; i++) {
      if (mpinStr[i] === mpinStr[i + 1] && mpinStr[i + 1] === mpinStr[i + 2]) {
        return false;
      }
    }
    return true;
  })();
  // No history available in-flow — treat as informational pass once 6 digits entered
  const mpinNotReused = mpinComplete;
  const mpinValid = mpinComplete && confirmMpinComplete && mpinsMatch && mpinNonConsecutive && mpinNoTriples;
  // Timers
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'mobile_otp' && mobileTimer > 0) {
      timer = setInterval(() => setMobileTimer(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, mobileTimer]);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'bvs' && (bvsPhase === 'right_camera' || bvsPhase === 'left_camera')) {
      if (bvsCameraTimer > 0) {
        timer = setInterval(() => setBvsCameraTimer(prev => prev - 1), 1000);
      } else {
        if (bvsPhase === 'right_camera') setBvsPhase('right_success');
        if (bvsPhase === 'left_camera') setBvsPhase('left_success');
      }
    }
    return () => clearInterval(timer);
  }, [step, bvsPhase, bvsCameraTimer]);
  useEffect(() => {
    let timer: NodeJS.Timeout;
    if (step === 'cooling_off' && coolingOffTime > 0) {
      timer = setInterval(() => setCoolingOffTime(prev => prev - 1), 1000);
    }
    return () => clearInterval(timer);
  }, [step, coolingOffTime]);
  // Handlers
  const handleNext = (nextStep: SignupStep) => setStep(nextStep);
  const handleBack = () => {
    switch (step) {
      case 'user_type':
        onBack();
        break;
      case 'terms':
        setStep('user_type');
        break;
      case 'details':
        setStep('terms');
        break;
      case 'mobile_otp':
        setStep('details');
        break;
      case 'email_otp':
        setStep('mobile_otp');
        break;
      case 'username':
        // Credit-card users skip the Confirm-email step on both forward + back.
        setStep(userType === 'bop_credit_card' ? 'mobile_otp' : 'email_otp');
        break;
      case 'password':
        setStep('username');
        break;
      case 'mpin':
        setStep('password');
        break;
      case 'bvs':
        setStep('mpin');
        break;
    }
  };
  const handleCnicChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^\d]/g, '');
    let formatted = digits;
    if (digits.length > 5) formatted = digits.slice(0, 5) + '-' + digits.slice(5);
    if (digits.length > 12) formatted = digits.slice(0, 5) + '-' + digits.slice(5, 12) + '-' + digits.slice(12);
    if (formatted.length <= 15) setCnic(formatted);
  };
  const handleOtpChange = (index: number, value: string, isEmail: boolean = false) => {
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const newOtp = isEmail ? [...emailOtp] : [...mobileOtp];
    newOtp[index] = digit;
    if (isEmail) setEmailOtp(newOtp);else setMobileOtp(newOtp);
    if (digit && index < 5) {
      const nextInput = document.getElementById(`${isEmail ? 'email' : 'mobile'}-otp-${index + 1}`);
      nextInput?.focus();
    }
    // Auto-advance Mobile OTP step when all 6 digits are filled.
    // Credit-card users skip the Confirm-email step entirely.
    if (!isEmail && index === 5 && digit) {
      const filled = newOtp.every(d => d !== '');
      if (filled) {
        const nextStep = userType === 'bop_credit_card' ? 'username' : 'email_otp';
        setTimeout(() => setStep(nextStep), 350);
      }
    }
  };
  const handleUserTypeSelect = (type: string) => {
    if (type !== 'bop_account' && type !== 'bop_credit_card') return; // Wallet still coming soon
    setUserType(type);
    setTimeout(() => setStep('terms'), 250);
  };
  const handleCreditCardChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 16);
    // Format as 4-4-4-4
    const formatted = digits.match(/.{1,4}/g)?.join(' ') ?? digits;
    setCreditCardNumber(formatted);
  };
  const handleExpiryChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const digits = e.target.value.replace(/[^\d]/g, '').slice(0, 4);
    let formatted = digits;
    if (digits.length >= 3) {
      formatted = digits.slice(0, 2) + '/' + digits.slice(2);
    }
    setCreditCardExpiry(formatted);
  };
  // MPIN digit input — OTP-style for both fields
  const handleMpinDigitChange = (index: number, value: string, isConfirm: boolean = false) => {
    const digit = value.replace(/[^\d]/g, '').slice(-1);
    const arr = isConfirm ? [...confirmMpin] : [...mpin];
    arr[index] = digit;
    if (isConfirm) setConfirmMpin(arr);else setMpin(arr);
    if (digit && index < 5) {
      const nextInput = document.getElementById(`${isConfirm ? 'cmpin' : 'mpin'}-${index + 1}`);
      nextInput?.focus();
    }
  };
  const handleMpinKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>, isConfirm: boolean = false) => {
    if (e.key === 'Backspace' && index > 0) {
      const arr = isConfirm ? confirmMpin : mpin;
      if (!arr[index]) {
        const prev = document.getElementById(`${isConfirm ? 'cmpin' : 'mpin'}-${index - 1}`);
        prev?.focus();
      }
    }
  };
  // Common Styles
  const bgStyle = isPlatinum ? 'bg-[#1C1C1E]' : isDark ? 'bg-[#1A0E08]' : 'bg-[#FFF5F0]';
  const textStyle = isPlatinum || isDark ? 'text-white' : 'text-[#1A1A1A]';
  const subtextStyle = isPlatinum ? 'text-[#A0A0A5]' : isDark ? 'text-[#B8A89E]' : 'text-[#5A5B5E]';
  const inputBgStyle = isPlatinum ? 'bg-[#3A3A3C]' : isDark ? 'bg-[#3D2518]' : 'bg-white';
  const inputBorderStyle = isPlatinum ? 'border-[rgba(160,160,165,0.15)]' : isDark ? 'border-[rgba(243,112,36,0.15)]' : 'border-[#F0E8E3]';
  const stepOrder: SignupStep[] = ['user_type', 'terms', 'details', 'mobile_otp', 'email_otp', 'username', 'password', 'mpin', 'bvs', 'cooling_off'];
  const stepIndex = stepOrder.indexOf(step);
  const totalSteps = stepOrder.length - 1; // Exclude cooling_off from progress
  const formatTime = (seconds: number) => {
    const h = Math.floor(seconds / 3600);
    const m = Math.floor(seconds % 3600 / 60);
    const s = seconds % 60;
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}:${s.toString().padStart(2, '0')}`;
  };
  return <div className="w-full min-h-screen flex flex-col relative overflow-hidden" style={{
    backgroundColor: '#FFFBFA'
  }} data-id="element-22" data-mp="7dseaj70bm3n">
      {/* Header */}
      {step !== 'cooling_off' && <div className="px-6 pt-6 pb-4 flex items-center gap-3 relative z-10" data-id="element-23" data-mp="3tt3lxk23wmx8">
          <button onClick={handleBack} className={`p-2 -ml-2 rounded-full transition-colors ${isDark || isPlatinum ? 'hover:bg-white/5' : 'hover:bg-black/5'}`} data-id="element-24" data-mp="3tqqxqp5r9gbq">
            <ArrowLeftIcon className={`w-6 h-6 ${textStyle}`} strokeWidth={2} data-id="element-25" data-mp="q4toy4hhp631" />
          </button>
          <h2 className={textStyle} style={{
        fontFamily: 'Poppins, system-ui, sans-serif',
        fontWeight: 500,
        fontSize: 18
      }} data-id="element-26" data-mp="lcick25kbrtl">
            Signup
          </h2>
        </div>}

      {/* 4-phase milestone progress bar */}
      {step !== 'cooling_off' && <div className="px-6 mb-6 relative z-10" data-id="element-27" data-mp="2d08ap5ghfk75">
          <RegistrationProgressBar currentStep={Math.min(stepIndex + 1, totalSteps)} totalSteps={totalSteps} onStepClick={target => {
        const targetIdx = target - 1;
        if (targetIdx < stepIndex) setStep(stepOrder[targetIdx]);
      }} data-id="element-28" data-mp="2czf3629p8qny" />
        </div>}

      {/* Content Area */}
      <div className="flex-1 px-6 flex flex-col relative z-10" data-id="element-29" data-mp="2rwfqltkuxzgm">
        <AnimatePresence mode="wait" data-id="element-30" data-mp="2woh39glxts76">
          {/* 1. User Type Selection (Auto-advance) */}
          {step === 'user_type' && <motion.div key="user_type" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-31" data-mp="30p10slafaxtu">
              <h1 className={`mb-2 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-32" data-mp="2x1rnlwowcoak">
                Lets get started
              </h1>
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-33" data-mp="36qt37lrj53o0">
                Sign-Up using your:
              </p>

              <div className="flex flex-col gap-3" data-id="element-34" data-mp="2942ld8gitb3y">
                <motion.button whileTap={{
              scale: 0.98
            }} onClick={() => handleUserTypeSelect('bop_account')} className="flex items-center gap-4 px-5 text-left transition-all" style={{
              width: 360,
              maxWidth: '100%',
              height: 92,
              borderRadius: 8,
              background: '#FFFFFF',
              border: userType === 'bop_account' ? '1px solid #F26428' : '1px solid transparent',
              boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
            }} data-id="element-35" data-mp="295ndysj9yjz6">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                background: '#FBE4D3'
              }} data-id="element-36" data-mp="audhn4lgzbt3">
                    <BuildingIcon className="w-6 h-6 text-[#F26428]" strokeWidth={1.8} data-id="element-37" data-mp="3t1t5h4p0q27m" />
                  </div>
                  <span className="text-[#1A1A1A]" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 14
              }} data-id="element-38" data-mp="ya6584qa0th">
                    Account number / IBAN
                  </span>
                </motion.button>

                <motion.button whileTap={{
              scale: 0.98
            }} onClick={() => handleUserTypeSelect('bop_credit_card')} className="flex items-center gap-4 px-5 text-left transition-all" style={{
              width: 360,
              maxWidth: '100%',
              height: 92,
              borderRadius: 8,
              background: '#FFFFFF',
              border: userType === 'bop_credit_card' ? '1px solid #F26428' : '1px solid transparent',
              boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
            }} data-id="element-39" data-mp="frdk75j41wfg">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                background: '#FBE4D3'
              }} data-id="element-40" data-mp="1d652ao9kdzyq">
                    <CreditCardIcon className="w-6 h-6 text-[#F26428]" strokeWidth={1.8} data-id="element-41" data-mp="189xv6s5fmif7" />
                  </div>
                  <span className="text-[#1A1A1A]" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 14
              }} data-id="element-42" data-mp="yi0kdmic5sh0">
                    Credit Card Details
                  </span>
                </motion.button>

                {/* Section divider */}
                <p className={`mt-6 mb-1 ${subtextStyle}`} style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 14
            }} data-id="element-43" data-mp="kgo4tc737svn">
                  Open an instant account with BOP:
                </p>

                <button disabled className="flex items-center gap-4 px-5 text-left transition-all relative cursor-not-allowed" style={{
              width: 360,
              maxWidth: '100%',
              height: 92,
              borderRadius: 8,
              background: '#FFFFFF',
              border: '1px solid transparent',
              boxShadow: '0 2px 4px rgba(0,0,0,0.06)'
            }} data-id="element-44" data-mp="adj2vbgrozwo">
                  <div className="w-12 h-12 rounded-full flex items-center justify-center shrink-0" style={{
                background: '#FBE4D3'
              }} data-id="element-45" data-mp="3ns5qn0zsl65g">
                    <WalletIcon className="w-6 h-6 text-[#F26428] opacity-70" strokeWidth={1.8} data-id="element-46" data-mp="1ddx15r829km" />
                  </div>
                  <span style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 14,
                color: '#9A9A9A'
              }} data-id="element-47" data-mp="303w7y7xvkyij">
                    DigiBOP Wallet
                  </span>
                  <span className="absolute top-3 right-3" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 10,
                color: '#6E6F72',
                background: '#EFEFEF',
                padding: '4px 10px',
                borderRadius: 999
              }} data-id="element-48" data-mp="n5cvlf9ipnvb">
                    Coming Soon
                  </span>
                </button>
              </div>
            </motion.div>}

          {/* 2. Terms & Conditions */}
          {step === 'terms' && <motion.div key="terms" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-49" data-mp="1fsgur3lxj7z6">
              <h1 className={`mb-6 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-50" data-mp="1poaf395w45ri">
                Terms & Conditions
              </h1>

              <div className={`flex p-1 rounded-full mb-6 ${inputBgStyle} border ${inputBorderStyle}`} data-id="element-51" data-mp="28ddw9rs2rcnl">
                <button onClick={() => setTermsLanguage('en')} className={`flex-1 py-2 rounded-full text-sm font-bold transition-all ${termsLanguage === 'en' ? 'bg-[#F37024] text-white shadow-md' : subtextStyle}`} data-id="element-52" data-mp="1ue4hmwjioqlq">
                  English
                </button>
                <button onClick={() => setTermsLanguage('ur')} className={`flex-1 py-2 rounded-full text-sm font-bold transition-all ${termsLanguage === 'ur' ? 'bg-[#F37024] text-white shadow-md' : subtextStyle}`} data-id="element-53" data-mp="1pgcqi4qp58il">
                  اردو
                </button>
              </div>

              <div ref={termsScrollRef} onScroll={e => {
            const el = e.currentTarget;
            const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 6;
            if (atBottom) setTermsScrolledToBottom(true);
          }} className={`flex-1 rounded-3xl p-6 mb-4 overflow-y-auto border ${inputBorderStyle}`} style={{
            background: isPlatinum ? '#3A3A3C' : isDark ? '#3D2518' : '#FFFFFF',
            maxHeight: 320
          }} data-id="element-54" data-mp="11qichjun5dwr">
                {termsLanguage === 'en' ? <div className="space-y-4" data-id="element-55" data-mp="10uv0kd7fj3ck">
                    <h3 className={`font-bold text-base ${textStyle}`} data-id="element-56" data-mp="10vmjanfxh6lf">
                      1. Acceptance of Terms
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-57" data-mp="1oms1385fi8yd">
                      By accessing and using the digiBOP mobile application, you
                      agree to be bound by these Terms and Conditions.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-58" data-mp="1jsebxnhd0y25">
                      2. User Registration
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-59" data-mp="27hgpaj4yo4qu">
                      You must provide accurate and complete information during
                      the registration process. You are responsible for the
                      accuracy of your CNIC, mobile number and account
                      information.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-60" data-mp="akb288crd1y9">
                      3. Privacy Policy
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-61" data-mp="3e1ji4mnxdrqc">
                      Your privacy is important to us. Our Privacy Policy
                      explains how we collect, use, store and protect your
                      personal information in line with State Bank of Pakistan
                      regulations.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-62" data-mp="3izcl76txm5me">
                      4. Account Security
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-63" data-mp="yaotvxrfkysb">
                      You are solely responsible for safeguarding your MPIN,
                      password and biometric credentials. Never share these with
                      anyone, including bank staff.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-64" data-mp="4x5l0uaphl5v">
                      5. Transaction Limits
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-65" data-mp="8yhejawpaqjc">
                      The Bank reserves the right to set, modify or apply daily
                      and monthly transaction limits at any time without prior
                      notice.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-66" data-mp="3hdixwahsdw80">
                      6. Service Availability
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-67" data-mp="2po6232p7i6nk">
                      Services may be unavailable due to scheduled maintenance,
                      network issues, or events beyond the Bank's control. The
                      Bank shall not be liable for any losses caused by such
                      downtime.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-68" data-mp="2h4t5ga7jvsze">
                      7. Fees and Charges
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-69" data-mp="2870m8uypk16q">
                      Applicable fees, taxes and charges (including FED) will be
                      deducted as per the Bank's prevailing Schedule of Charges.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-70" data-mp="1yel705espqpj">
                      8. Fraud Prevention
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-71" data-mp="1pgraouh16x2d">
                      You agree to immediately report any suspicious activity,
                      unauthorized transactions or device theft to the digiBOP
                      helpline. The Bank may block any account suspected of
                      fraudulent activity.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-72" data-mp="3kje7g8o51jci">
                      9. Data Sharing
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-73" data-mp="35tgoc752fju6">
                      You authorize the Bank to share necessary information with
                      regulators, NADRA, telecom partners and payment schemes
                      (RAAST, 1LINK, VISA, Mastercard) for KYC and transaction
                      processing.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-74" data-mp="2mvxa8p3r3b0w">
                      10. Termination of Service
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-75" data-mp="2rw1wkvrcazz1">
                      The Bank may, at its sole discretion, suspend or terminate
                      your access to digiBOP at any time, with or without
                      notice, in case of breach of these Terms.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-76" data-mp="25m1tfoxols48">
                      11. Amendments
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-77" data-mp="2kbhk74x7pxjx">
                      The Bank reserves the right to modify these Terms at any
                      time. Continued use of the app constitutes acceptance of
                      the revised Terms.
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-78" data-mp="2obhxgm8ks2pe">
                      12. Governing Law
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-79" data-mp="3377jgkdrgfu4">
                      These Terms shall be governed by and construed in
                      accordance with the laws of the Islamic Republic of
                      Pakistan. Any disputes shall be subject to the
                      jurisdiction of courts in Lahore.
                    </p>
                    <p className={`text-xs italic pt-3 ${subtextStyle}`} style={{
                textAlign: 'center'
              }} data-id="element-80" data-mp="1tmemfcs4kbol">
                      — End of Terms & Conditions —
                    </p>
                  </div> : <div className="space-y-4 text-right" dir="rtl" data-id="element-81" data-mp="22nmkbtqqbai5">
                    <h3 className={`font-bold text-base ${textStyle}`} data-id="element-82" data-mp="27lxt015afeys">
                      1. شرائط کی منظوری
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-83" data-mp="2gl1d98g6y0ev">
                      ڈیجی بی او پی موبائل ایپلیکیشن تک رسائی اور استعمال کر کے،
                      آپ ان شرائط و ضوابط کے پابند ہونے سے اتفاق کرتے ہیں۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-84" data-mp="2q9jc3k4wtxfv">
                      2. صارف کی رجسٹریشن
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-85" data-mp="3091s5php8z2n">
                      آپ کو رجسٹریشن کے عمل کے دوران درست اور مکمل معلومات فراہم
                      کرنی ہوں گی۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-86" data-mp="jm2gtalzpzal">
                      3. اکاؤنٹ کی حفاظت
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-87" data-mp="eo0umgt9pjuw">
                      اپنا MPIN، پاس ورڈ اور بائیومیٹرک تفصیلات کسی کے ساتھ شیئر
                      نہ کریں، بشمول بینک عملہ۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-88" data-mp="17e0we6sh2gtj">
                      4. لین دین کی حدود
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-89" data-mp="xlbjr8l6qwwm">
                      بینک کسی بھی وقت روزانہ اور ماہانہ لین دین کی حدود کا تعین
                      یا تبدیل کرنے کا حق محفوظ رکھتا ہے۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-90" data-mp="1q4qxpr2e9vka">
                      5. فیس اور چارجز
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-91" data-mp="tnupy1n8pvux">
                      قابل اطلاق فیس، ٹیکس اور چارجز بینک کے رائج شیڈول آف چارجز
                      کے مطابق وصول کیے جائیں گے۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-92" data-mp="kp9c2jspr50h">
                      6. دھوکہ دہی کی روک تھام
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-93" data-mp="1dcn01sefoq8g">
                      مشکوک سرگرمی، غیر مجاز لین دین یا ڈیوائس چوری کی فوری
                      اطلاع ڈیجی بی او پی ہیلپ لائن کو دیں۔
                    </p>
                    <h3 className={`font-bold text-base ${textStyle} pt-2`} data-id="element-94" data-mp="13de9zamovvrd">
                      7. ترامیم
                    </h3>
                    <p className={`text-sm leading-relaxed ${subtextStyle}`} data-id="element-95" data-mp="18fdcyjeioqns">
                      بینک کسی بھی وقت ان شرائط میں ترمیم کرنے کا حق محفوظ رکھتا
                      ہے۔ مسلسل استعمال نظرثانی شدہ شرائط کی منظوری کے مترادف
                      ہوگا۔
                    </p>
                    <p className={`text-xs italic pt-3 ${subtextStyle}`} style={{
                textAlign: 'center'
              }} data-id="element-96" data-mp="3li5po693fvdr">
                      — شرائط و ضوابط ختم —
                    </p>
                  </div>}
              </div>

              {!termsScrolledToBottom}

              <label className="flex items-start gap-4 mb-6 group cursor-pointer" onClick={e => {
            if (!termsScrolledToBottom && termsScrollRef.current) {
              e.preventDefault();
              const el = termsScrollRef.current;
              el.scrollTo({
                top: el.scrollHeight,
                behavior: 'smooth'
              });
              setTermsScrolledToBottom(true);
              setTermsAccepted(true);
            }
          }} data-id="element-97" data-mp="3pm3oefuib46a">
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center mt-0.5 shrink-0 transition-all ${termsAccepted ? 'bg-[#F37024] border-[#F37024]' : `border-[#F37024] ${isPlatinum || isDark ? 'bg-transparent' : 'bg-white'} group-hover:border-[#D95A1A]`}`} data-id="element-98" data-mp="2j4gk48aro2gf">
                  {termsAccepted && <CheckCircle2Icon className="w-4 h-4 text-white" strokeWidth={3} data-id="element-99" data-mp="17emszos08jit" />}
                </div>
                <span className={`text-sm leading-snug font-medium ${textStyle}`} data-id="element-100" data-mp="12hecrtqf2pk7">
                  I have read and agree to the Terms and Conditions and Privacy
                  Policy of Bank of Punjab.
                </span>
                <input type="checkbox" className="hidden" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)} data-id="element-101" data-mp="1v360w3756uad" />
              </label>

              <div className="mt-auto pb-8 flex flex-col items-center gap-3" data-id="element-102" data-mp="1m3j3dshja8ml">
                <RegistrationCTAButton onClick={() => handleNext('details')} disabled={!termsAccepted} data-id="element-103" data-mp="1h60q5pwhh78s">
                  Accept & Continue
                </RegistrationCTAButton>
                <button onClick={onBack} style={{
              width: 278,
              maxWidth: '100%',
              height: 56,
              borderRadius: 8,
              background: 'transparent',
              border: '1px solid #661806',
              color: '#661806',
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 16,
              cursor: 'pointer',
              transition: 'background 0.2s ease'
            }} data-id="element-104" data-mp="5qc36yj8c87z">
                  Decline
                </button>
              </div>
            </motion.div>}

          {/* 3. Combined Details: CNIC + Account + Referral */}
          {step === 'details' && <motion.div key="details" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-105" data-mp="355ctdp83z8v3">
              <h1 className={`mb-1 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-106" data-mp="1fp3m1x9ak5yo">
                Confirm your identity
              </h1>
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-107" data-mp="2i1zwl77wf3va">
                We need a few details to securely link your account.
              </p>

              {/* CNIC */}
              <div className="mb-5" data-id="element-108" data-mp="35zffgqpqj856">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-109" data-mp="35ye1oehc47jr">
                  CNIC / NICOP / POC
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center gap-3 px-5`} style={{
              width: 364.8,
              maxWidth: '100%',
              height: 54.5,
              borderRadius: 8
            }} data-id="element-110" data-mp="3aq5j9ziij2xr">
                  <UserIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2} data-id="element-111" data-mp="3jp92r4lldkcs" />
                  <input type="text" value={cnic} onChange={handleCnicChange} placeholder="XXXXX-XXXXXXX-X" className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-112" data-mp="75euy197uppm" />
                  {cnic.replace(/\D/g, '').length === 13 && <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-113" data-mp="26tp9mb73dts" />}
                </div>
              </div>

              {/* Account Number / IBAN OR Credit Card based on user type */}
              {userType === 'bop_credit_card' ? <>
                  {/* Credit Card Number */}
                  <div className="mb-5" data-id="element-114" data-mp="3apsz5sl6udcd">
                    <label className="mb-2 block" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#661806'
              }} data-id="element-115" data-mp="3fnaziul53k8r">
                      Credit Card Number
                    </label>
                    <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center gap-3 px-5`} style={{
                width: 364.8,
                maxWidth: '100%',
                height: 54.5,
                borderRadius: 8
              }} data-id="element-116" data-mp="2ylnl2ug6pz7h">
                      <CreditCardIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2} data-id="element-117" data-mp="2ou138f9cd1vr" />
                      <input type="text" inputMode="numeric" value={creditCardNumber} onChange={handleCreditCardChange} placeholder="0000 0000 0000 0000" className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-118" data-mp="2ksfp6ql2mhjz" />
                      {creditCardNumber.replace(/\s/g, '').length === 16 && <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-119" data-mp="21w64hkfnysr0" />}
                    </div>
                  </div>

                  {/* Expiry Date */}
                  <div className="mb-5" data-id="element-120" data-mp="43k1dft3wqoe">
                    <label className="mb-2 block" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#661806'
              }} data-id="element-121" data-mp="41e4ddtc3ie">
                      Expiry Date
                    </label>
                    <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center gap-3 px-5`} style={{
                width: 364.8,
                maxWidth: '100%',
                height: 54.5,
                borderRadius: 8
              }} data-id="element-122" data-mp="1rrhdb3lpmee4">
                      <ClockIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2} data-id="element-123" data-mp="141ve4g5c7z64" />
                      <input type="text" inputMode="numeric" value={creditCardExpiry} onChange={handleExpiryChange} placeholder="MM/YY" maxLength={5} className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-124" data-mp="183qhrraqqon2" />
                      {creditCardExpiry.length === 5 && <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-125" data-mp="2ohqd0oyn5q3z" />}
                    </div>
                  </div>
                </> : <div className="mb-5" data-id="element-126" data-mp="oefmwifu2awg">
                  <div className="flex items-center gap-1.5 mb-2" data-id="element-127" data-mp="ohlga9vhvyxz">
                    <label style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#661806'
              }} data-id="element-128" data-mp="kdlyzp2e4w3k">
                      Account Number or IBAN
                    </label>
                    <button type="button" onClick={() => setShowIbanInfo(v => !v)} aria-label="What is an IBAN?" className="flex items-center justify-center" style={{
                color: '#661806'
              }} data-id="element-129" data-mp="5q0ym2wwhazc">
                      <InfoIcon className="w-4 h-4" strokeWidth={2} data-id="element-130" data-mp="fgdgfndbyq1s" />
                    </button>
                  </div>
                  {showIbanInfo && <div className="mb-2 p-3 rounded-lg relative" style={{
              background: '#FFF3EC',
              border: '1px solid rgba(102,24,6,0.15)',
              maxWidth: 364.8
            }} data-id="element-131" data-mp="3j3vyz4whepsx">
                      <p style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 12,
                color: '#661806',
                marginBottom: 4
              }} data-id="element-132" data-mp="1tmi8tmbg4jgm">
                        IBAN is 24 characters
                      </p>
                      <p style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 11,
                lineHeight: 1.5,
                color: '#6E6F72'
              }} data-id="element-133" data-mp="1ooyxau64uksd">
                        A Pakistani IBAN is exactly 24 characters long, starting
                        with PK, followed by 2 check digits, a 4-digit bank
                        identifier, and your 16-digit account number.
                      </p>
                    </div>}
                  <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center gap-3 px-5`} style={{
              width: 364.8,
              maxWidth: '100%',
              height: 54.5,
              borderRadius: 8
            }} data-id="element-134" data-mp="rw3fif8opb61">
                    <BuildingIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2} data-id="element-135" data-mp="1jkpy4qujseu6" />
                    <input type="text" value={accountNumber} onChange={e => setAccountNumber(e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''))} placeholder="Enter account no. or PK00BOPM..." className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-136" data-mp="1elmr50v0byjk" />
                    {accountNumber.length >= 14 && <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-137" data-mp="22cipyhxvru0b" />}
                  </div>
                  <p className={`text-[11px] mt-1.5 ml-2 ${subtextStyle}`} data-id="element-138" data-mp="1xixp8i4wklxf">
                    Accepts both 14-digit account number and 24-character IBAN
                  </p>
                  {/* RDA indicator — when account number starts with 0000 */}
                  {accountNumber.length >= 4 && accountNumber.startsWith('0000') && <div className="mt-2 flex items-center gap-1.5" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 12,
              color: '#22C55E',
              marginLeft: 8
            }} data-id="element-139" data-mp="2slrd41ws1ldh">
                        <CheckCircleIcon className="w-4 h-4 text-[#22C55E] shrink-0" strokeWidth={2.2} data-id="element-140" data-mp="32m0avk6f3euz" />
                        RDA account
                      </div>}
                </div>}

              {/* Referral */}
              <div className="mb-5" data-id="element-141" data-mp="3gjow4t4eco0k">
                <label className="mb-2 block" style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13,
              color: '#661806'
            }} data-id="element-142" data-mp="3gin87o2a871f">
                  Referral Code{' '}
                  <span className="opacity-60 font-medium normal-case tracking-normal" data-id="element-143" data-mp="3v8l1ujizdoy0">
                    (Optional)
                  </span>
                </label>
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center gap-3 px-5`} style={{
              width: 364.8,
              maxWidth: '100%',
              height: 54.5,
              borderRadius: 8
            }} data-id="element-144" data-mp="35bv8ib4e8t9">
                  <GiftIcon className="w-5 h-5 text-[#F37024] shrink-0" strokeWidth={2} data-id="element-145" data-mp="1ge9qywiyjj17" />
                  <input type="text" value={referralCode} onChange={e => setReferralCode(e.target.value.toUpperCase())} placeholder="e.g. BONUS50" className={`w-full outline-none text-base bg-transparent font-medium tracking-wider ${textStyle} placeholder:opacity-40`} data-id="element-146" data-mp="1lee5wi47w5pb" />
                  {referralCode.length > 3 && <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0" data-id="element-147" data-mp="xnqw1raqr604" />}
                </div>
              </div>

              <div className="mt-auto pb-8 pt-4 flex justify-center" data-id="element-148" data-mp="12oejk2zrh4pl">
                <RegistrationCTAButton onClick={() => {
              // RDA accounts skip the mobile OTP step
              if (accountNumber.startsWith('0000')) {
                // Pre-fill encrypted registered email + auto-send OTP
                setEmail('K***@gmail.com');
                setEmailOtpSent(true);
                setEmailOtpTimer(60);
                handleNext('email_otp');
              } else {
                handleNext('mobile_otp');
              }
            }} disabled={cnic.replace(/\D/g, '').length !== 13 || (userType === 'bop_credit_card' ? creditCardNumber.replace(/\s/g, '').length !== 16 || creditCardExpiry.length !== 5 : accountNumber.length < 14)} data-id="element-149" data-mp="293p8m554oa63">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {/* 4. Mobile OTP */}
          {step === 'mobile_otp' && <motion.div key="mobile_otp" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-150" data-mp="35w5ejf2e69n0">
              <div className="hidden" data-id="element-151" data-mp="z57e6pkgqbiz">
                <motion.div className="hidden" data-id="element-152" data-mp="z68pmv5e5018">
                  <SmartphoneIcon className="w-10 h-10 text-[#F37024]" strokeWidth={1.5} data-id="element-153" data-mp="2tl5u1dn4jj5h" />
                </motion.div>
              </div>

              <h1 className={`mb-2 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-154" data-mp="2ydyxzzarqngi">
                Verify Mobile Number
              </h1>
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-155" data-mp="3cbvy35oflbzx">
                We've sent an OTP to your registered number: +92•••••••66.
              </p>

              <div className="flex items-center justify-center gap-2 mb-4" data-id="element-156" data-mp="37csqpekzatos">
                <span className="text-xs font-bold text-[#22C55E] uppercase tracking-wider" data-id="element-157" data-mp="37ex5irkf1d3u"></span>
              </div>

              <div className="flex justify-between gap-2 mb-8 p-3 rounded-2xl bg-[#F9F9F9]" style={{
            background: 'rgba(249,249,249,100)'
          }} data-id="element-158" data-mp="1vxnn88d0wr44">
                {mobileOtp.map((digit, index) => <div key={index} className="relative group flex-1" data-id="element-159" data-mp="1jfjunc2n11uf" data-mpi={index}>
                    <input id={`mobile-otp-${index}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value)} className={`relative w-full h-14 rounded-2xl text-center text-2xl font-bold outline-none border transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10' : `${inputBorderStyle} ${inputBgStyle}`} ${textStyle}`} data-id="element-160" data-mp="1jhe3vt4jr5io" />
                  </div>)}
              </div>
              

              <div className="flex flex-col items-center justify-center gap-2" data-id="element-161" data-mp="1fdyad5nrxla7">
                {mobileTimer > 0 ? <div className="flex items-center gap-2" data-id="element-162" data-mp="1ampze56sprux">
                      <span style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 13,
                color: '#6E6F72'
              }} data-id="element-163" data-mp="1al594rrikv2v">
                        Resend OTP in{' '}
                        <span style={{
                  color: '#661806',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-164" data-mp="3hdminey2zpk3">
                          0:{String(emailOtpTimer).padStart(2, '0')}
                        </span>
                      </span>
                      
                      

                    
                  </div> : <button className="text-[#F37024] font-bold text-sm hover:underline" data-id="element-165" data-mp="3wdinch80fmr">
                    Resend OTP
                  </button>}
              </div>

              <div className="mt-auto pb-8 pt-4 text-center" data-id="element-166" data-mp="cy3jf8wzsqx7">
                <p className={`text-[12px] ${subtextStyle}`} data-id="element-167" data-mp="cywj5pxjag5u"></p>
              </div>
            </motion.div>}

          {/* 5. Email OTP */}
          {step === 'email_otp' && <motion.div key="email_otp" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-168" data-mp="2eq9p8ib8mu3h">
              <h1 className={`mb-2 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-169" data-mp="2c74s22wlx3yu">
                {accountNumber.startsWith('0000') ? 'Verify your registered email' : 'Confirm your email'}
              </h1>
              <p className={`mb-8 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14
          }} data-id="element-170" data-mp="2v462i38xjwa6">
                {accountNumber.startsWith('0000') ? 'We sent a one-time code to the email on file for your RDA account. Enter it below to continue.' : 'We need your email for added security (optional)'}
              </p>

              <div className="relative mb-6" data-id="element-171" data-mp="19rmhsv7890w5">
                <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex flex-col justify-center px-5`} style={{
              width: 364.8,
              maxWidth: '100%',
              height: 54.5,
              borderRadius: 8
            }} data-id="element-172" data-mp="19ogv1mm2mb3l">
                  <label className={`transition-all duration-200 ${email ? 'opacity-100 mb-1' : 'opacity-0 h-0 overflow-hidden'}`} style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#661806'
              }} data-id="element-173" data-mp="1c3x3jm5gnwus">
                    Email Address
                  </label>
                  <input type="email" value={email} onChange={e => setEmail(e.target.value)} disabled={emailOtpSent || accountNumber.startsWith('0000')} readOnly={accountNumber.startsWith('0000')} placeholder="Enter your email address" className={`w-full outline-none text-lg bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-base placeholder:font-normal disabled:opacity-50`} data-id="element-174" data-mp="t6m3w0fx31et" />
                </div>
              </div>

              {!emailOtpSent ? <button onClick={() => {
            setEmailOtpSent(true);
            setEmailOtpTimer(60);
          }} disabled={!email || !email.includes('@')} className="border-2 border-[#F37024] text-[#F37024] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed ml-[40px]" style={{
            width: 278,
            maxWidth: '100%',
            height: 58,
            borderRadius: 8,
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 16
          }} data-id="element-175" data-mp="9k7q0lv53paf">
                  Send OTP
                </button> : <motion.div initial={{
            opacity: 0,
            height: 0
          }} animate={{
            opacity: 1,
            height: 'auto'
          }} className="space-y-6" data-id="element-176" data-mp="4s16oonhr04q">
                  <div className="flex items-center justify-center gap-2" data-id="element-177" data-mp="elrppe0umid1">
                    <span className="text-xs font-bold text-[#22C55E] uppercase tracking-wider" data-id="element-178" data-mp="sjy43aq71pmb"></span>
                  </div>
                  <div className="flex justify-between gap-2 p-3 rounded-2xl bg-[#F9F9F9] pt-[50px] pb-[50px]" style={{
              background: 'rgba(136,134,134,0.12)'
            }} data-id="element-179" data-mp="niijfjrxhl4g">
                    {emailOtp.map((digit, index) => <div key={index} className="relative group flex-1" data-id="element-180" data-mp="23xqq7bd9r25r" data-mpi={index}>
                        <input id={`email-otp-${index}`} type="password" inputMode="numeric" maxLength={1} value={digit} onChange={e => handleOtpChange(index, e.target.value, true)} className={`relative w-full h-14 rounded-2xl text-center text-2xl font-bold outline-none border transition-colors ${digit ? 'border-[#F37024] bg-[#F37024]/10' : `${inputBorderStyle} ${inputBgStyle}`} ${textStyle}`} data-id="element-181" data-mp="23vo0ytjxxd23" />
                      </div>)}
                  </div>

                  {/* Resend OTP timer */}
                  <div className="flex items-center justify-center gap-1.5 text-center" data-id="element-182" data-mp="1pzspv9vs9699">
                    {emailOtpTimer > 0 ? <span style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 400,
                fontSize: 13,
                color: '#6E6F72'
              }} data-id="element-183" data-mp="sghghgjxzohn">
                        Resend OTP in{' '}
                        <span style={{
                  color: '#661806',
                  fontWeight: 500,
                  fontVariantNumeric: 'tabular-nums'
                }} data-id="element-184" data-mp="l045tdv3yii3">
                          0:{String(emailOtpTimer).padStart(2, '0')}
                        </span>
                      </span> : <button type="button" onClick={() => {
                setEmailOtp(['', '', '', '', '', '']);
                setEmailOtpTimer(60);
              }} style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#F37024',
                background: 'transparent',
                border: 'none',
                cursor: 'pointer',
                padding: 0
              }} data-id="element-185" data-mp="7034jxaxl923">
                        Resend OTP
                      </button>}
                  </div>
                </motion.div>}

              <div className="mt-auto pb-8 pt-4 flex flex-col items-center gap-4" data-id="element-186" data-mp="1fe4z79ykzo3n">
                {accountNumber.startsWith('0000')}
                <RegistrationCTAButton onClick={() => handleNext('username')} disabled={accountNumber.startsWith('0000') ? !emailOtpSent || emailOtp.some(d => d === '') : emailOtpSent && emailOtp.some(d => d === '')} data-id="element-187" data-mp="1aipogqvve8bg">
                  {accountNumber.startsWith('0000') ? 'Verify & Continue' : emailOtpSent ? 'Verify & Continue' : 'Skip & Continue'}
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {/* 6. Username — requirements ABOVE textbox, suggestions BELOW */}
          {step === 'username' && <motion.div key="username" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-188" data-mp="10q0jmeutrsp3">
              <h1 className={`mb-1 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-189" data-mp="8x9t8kztoy7r">
                Pick a unique username
              </h1>

              {/* Requirement chips ABOVE textbox — symmetric grid like password */}
              <div className="grid grid-cols-2 gap-2 mb-5" data-id="element-190" data-mp="3q2s3oaprmi47">
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${username.length >= 6 ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-191" data-mp="hlxa5luuya2s">
                  6+ Characters
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${username.length > 0 && usernameNoSpace ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-192" data-mp="7nhjtl3nn4uc">
                  No Space
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${isUsernameAvailable ? 'bg-[#22C55E]/20 text-[#22C55E]' : isUsernameTaken && usernameTouched ? 'bg-red-500/20 text-red-500' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-193" data-mp="2xemhrkmz6n2s">
                  {'Available'}
                </div>
              </div>

              {/* Username textbox */}
              <div className={`relative border ${isUsernameTaken && usernameTouched ? 'border-red-500' : isUsernameAvailable ? 'border-[#22C55E]' : inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
            width: 364.8,
            maxWidth: '100%',
            height: 54.5,
            borderRadius: 8
          }} data-id="element-194" data-mp="p4wt5qeepzlm">
                <input type="text" value={username} onChange={e => {
              setUsername(e.target.value.toLowerCase().replace(/[^a-z0-9]/g, ''));
              setUsernameTouched(true);
            }} placeholder="username" className={`w-full outline-none bg-transparent ${textStyle} placeholder:opacity-40`} style={{
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 500,
              fontSize: 13
            }} data-id="element-195" data-mp="66k7542ecv8m" />
                {username.length >= 6 && (isUsernameAvailable ? <CheckCircleIcon className="w-6 h-6 text-[#22C55E] shrink-0" data-id="element-196" data-mp="2hu783xof8gez" /> : isUsernameTaken ? <XCircleIcon className="w-6 h-6 text-red-500 shrink-0" data-id="element-197" data-mp="2hw9xcfhr25in" /> : null)}
              </div>

              {/* Inline status text */}
              {username.length > 0}

              {/* Suggestions BELOW textbox — toned down */}
              {usernameSuggestions.length > 0 && <div className="mt-2" data-id="element-198" data-mp="1u4lahenp6993">
                  <span className={`text-[11px] font-medium ${subtextStyle} ml-1`} data-id="element-199" data-mp="1z3piw32ss2yd">
                    Available Suggestions
                  </span>
                  <div className="flex flex-wrap gap-1.5 mt-2" data-id="element-200" data-mp="2443qjk0hlavv">
                    {usernameSuggestions.map(suggestion => <button key={suggestion} onClick={() => {
                setUsername(suggestion);
                setUsernameTouched(true);
              }} className={`px-3 py-1.5 rounded-full text-[12px] border transition-colors ${inputBorderStyle} ${subtextStyle} hover:border-[#F37024] hover:text-[#F37024]`} style={{
                background: 'transparent'
              }} data-id="element-201" data-mp="3ai35l4ltecct">
                        {suggestion}
                      </button>)}
                  </div>
                </div>}

              <div className="mt-auto pb-8 pt-4 flex justify-center" data-id="element-202" data-mp="3i0ssd3tq6yhi">
                <RegistrationCTAButton onClick={() => handleNext('password')} disabled={!isUsernameAvailable} data-id="element-203" data-mp="3w0ttmkdwk7xi">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {/* 7. Password — requirements ABOVE textbox, plus Confirm Password */}
          {step === 'password' && <motion.div key="password" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-204" data-mp="4nzhhbbwq8p6">
              <h1 className={`mb-5 ${textStyle}`} style={{
            fontFamily: 'Poppins, ui-sans-serif, system-ui, sans-serif',
            fontSize: 20,
            fontWeight: 300
          }} data-id="element-205" data-mp="y6nlyexz25wh">
                Set a strong password
              </h1>

              {/* Requirements ABOVE textbox */}
              <div className="grid grid-cols-2 gap-2 mb-5" data-id="element-206" data-mp="t7azhvb0j0uf">
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdLength ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-207" data-mp="t9x5hqi998aj">
                  8+ Characters
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdUpper ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-208" data-mp="5jt0pftbz2hy">
                  1 Uppercase
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdNum ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-209" data-mp="j5c10skqxqw">
                  1 Number
                </div>
                <div className={`px-3 py-2 rounded-xl text-[11px] font-bold flex items-center gap-1.5 transition-colors ${pwdSpec ? 'bg-[#22C55E]/20 text-[#22C55E]' : `${inputBgStyle} ${subtextStyle}`}`} data-id="element-210" data-mp="38rwgnme5xofk">
                  1 Special Character
                </div>
              </div>

              {/* Password */}
              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-211" data-mp="bl86n47ckwe">
                Password
              </label>
              <div className={`relative border ${inputBorderStyle} ${inputBgStyle} flex items-center px-5 mb-3`} style={{
            width: 364.8,
            maxWidth: '100%',
            height: 54.5,
            borderRadius: 8
          }} data-id="element-212" data-mp="3hkrgg0kv2rxy">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-213" data-mp="268z123kwvqon" />
                <input type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-214" data-mp="lq6aci7azzzq" />
                <button onClick={() => setShowPassword(!showPassword)} className="ml-2 shrink-0 p-1" data-id="element-215" data-mp="15d41h06sdcbt">
                  {showPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-216" data-mp="19d4qferih5x4" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-217" data-mp="19g0px7j0c2no" />}
                </button>
              </div>

              {/* Confirm Password */}
              <label className="mb-2 block" style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 500,
            fontSize: 13,
            color: '#661806'
          }} data-id="element-218" data-mp="1jbaofla75rwp">
                Confirm Password
              </label>
              <div className={`relative border ${confirmPassword.length > 0 && pwdsMatch ? 'border-[#22C55E]' : confirmPassword.length > 0 && !pwdsMatch ? 'border-red-500' : inputBorderStyle} ${inputBgStyle} flex items-center px-5`} style={{
            width: 364.8,
            maxWidth: '100%',
            height: 54.5,
            borderRadius: 8
          }} data-id="element-219" data-mp="1qvlcru2uax8r">
                <LockIcon className="w-5 h-5 text-[#F37024] mr-3 shrink-0" data-id="element-220" data-mp="1voo9uc6tsotp" />
                <input type={showConfirmPassword ? 'text' : 'password'} value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} placeholder="Re-enter password" className={`w-full outline-none text-base tracking-widest bg-transparent font-medium ${textStyle} placeholder:opacity-50 placeholder:text-sm placeholder:font-normal placeholder:tracking-normal`} data-id="element-221" data-mp="17xscz0nij3fl" />
                {confirmPassword.length > 0 && (pwdsMatch ? <CheckCircleIcon className="w-5 h-5 text-[#22C55E] shrink-0 mr-2" data-id="element-222" data-mp="2tcg879hmdw2v" /> : <XCircleIcon className="w-5 h-5 text-red-500 shrink-0 mr-2" data-id="element-223" data-mp="2oah2vu2bkp3a" />)}
                <button onClick={() => setShowConfirmPassword(!showConfirmPassword)} className="ml-1 shrink-0 p-1" data-id="element-224" data-mp="2vui911g5srk7">
                  {showConfirmPassword ? <EyeOffIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-225" data-mp="30v5yhioqp0cb" /> : <EyeIcon className={`w-5 h-5 ${subtextStyle}`} data-id="element-226" data-mp="2mulieua7limi" />}
                </button>
              </div>

              {confirmPassword.length > 0 && !pwdsMatch && <p className="text-xs text-red-500 font-medium mt-2 ml-2" data-id="element-227" data-mp="2qxrkrqu7apnp">
                  Passwords do not match
                </p>}

              <div className="mt-auto pb-8 pt-4 flex justify-center" data-id="element-228" data-mp="3jkvsi3kohi36">
                <RegistrationCTAButton onClick={() => handleNext('mpin')} disabled={pwdValidCount < 4 || !pwdsMatch} data-id="element-229" data-mp="3jm6ubxukcfrn">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {/* 8. MPIN — both fields visible at once like password screen */}
          {step === 'mpin' && <motion.div key="mpin" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col" data-id="element-230" data-mp="65zgow065erc">
              {/* Hero icon */}
              <h1 className={`mb-2 ${textStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 300,
            fontSize: 20,
            lineHeight: 1.3
          }} data-id="element-231" data-mp="1o0qubma0jqr3">
                Create your MPIN
              </h1>
              <p className={`mb-5 ${subtextStyle}`} style={{
            fontFamily: 'Poppins, system-ui, sans-serif',
            fontWeight: 400,
            fontSize: 14,
            lineHeight: 1.5
          }} data-id="element-232" data-mp="1eyhnb6dyefig">
                A 6-digit code to log in and approve your transactions securely.
              </p>

              {/* MPIN strength checks */}
              <div className="flex flex-wrap mb-6 pl-[0px] pr-[0px] gap-[8px]" data-id="element-233" data-mp="2zfe2by34rj4w">
                {['Non Consecutive Numbers', 'No Triple Repeating Digits', "Can't Reuse last 3 PIN"].map(label => <div key={label} className="flex items-center justify-center px-3 pl-[0px] pr-[0px] ml-[0px] mr-[0px]" style={{
              width: 170,
              height: 35,
              borderRadius: 8,
              background: '#F5F5F5',
              fontFamily: 'Poppins, system-ui, sans-serif',
              fontWeight: 400,
              fontSize: 11,
              color: '#8F8F8F'
            }} data-id="element-234" data-mp="srnbllgeclmj">
                    <span className="leading-tight text-center" data-id="element-235" data-mp="1bhlpii6vl8y3">{label}</span>
                  </div>)}
              </div>

              {/* MPIN container — single grey surface holding both Enter + Confirm */}
              <div className="mb-4 flex flex-col justify-center pl-[5px] pr-[5px]" style={{
            width: 352,
            maxWidth: '100%',
            height: 289,
            borderRadius: 8,
            background: '#F9F9F9',
            padding: '50px 8px'
          }} data-id="element-236" data-mp="1lgkrjhx4y6c4">
                {/* Set MPIN */}
                <div className="mb-5" data-id="element-237" data-mp="3i9wb1fe7g2tj">
                  <label className="mb-3 block" style={{
                fontFamily: 'Poppins, system-ui, sans-serif',
                fontWeight: 500,
                fontSize: 13,
                color: '#661806'
              }} data-id="element-238" data-mp="3iaxmhkz4urbs">
                    Enter MPIN
                  </label>
                  <div className="flex gap-[6px]" data-id="element-239" data-mp="1v8903j8ezq8q">
                    {mpin.map((digit, index) => {
                  const isFilled = digit !== '';
                  return <motion.div key={index} animate={isFilled ? {
                    scale: [1, 1.08, 1]
                  } : {
                    scale: 1
                  }} transition={{
                    duration: 0.2
                  }} className="relative" data-id="element-240" data-mp="25399scqapedn" data-mpi={index}>
                          <input id={`mpin-${index}`} type="text" inputMode="numeric" autoComplete="off" maxLength={1} value={digit ? '•' : ''} onChange={e => handleMpinDigitChange(index, e.target.value)} onKeyDown={e => handleMpinKeyDown(index, e)} onFocus={e => e.target.select()} className={`text-center text-[22px] font-bold outline-none border transition-all duration-200 caret-transparent ${isFilled ? 'border-[#F37024] text-[#F37024]' : `${inputBorderStyle} ${textStyle} focus:border-[#F37024]`}`} style={{
                      width: 50,
                      height: 60,
                      borderRadius: 8,
                      background: '#FFFFFF'
                    }} data-id="element-241" data-mp="2ivh0bmqzk93w" />
                        </motion.div>;
                })}
                  </div>
                </div>

                {/* Confirm MPIN */}
                <div data-id="element-242" data-mp="3gq9m5lg5hcvn">
                  <div className="flex items-center justify-between mb-3" data-id="element-243" data-mp="3gprteysp5gsh">
                    <label style={{
                  fontFamily: 'Poppins, system-ui, sans-serif',
                  fontWeight: 500,
                  fontSize: 13,
                  color: '#661806'
                }} data-id="element-244" data-mp="3bxgjd348sem7">
                      Confirm MPIN
                    </label>
                    {confirmMpinComplete && mpinComplete && mpinsMatch && <motion.span initial={{
                  opacity: 0,
                  scale: 0.9
                }} animate={{
                  opacity: 1,
                  scale: 1
                }} className="flex items-center gap-1 text-[11px] font-medium text-[#22C55E]" data-id="element-245" data-mp="3glqmgh5salu">
                        <CheckCircle2Icon className="w-3.5 h-3.5" strokeWidth={2.5} data-id="element-246" data-mp="3b77m5fibpmpe" />
                        Match
                      </motion.span>}
                  </div>
                  <div className="flex gap-[6px]" data-id="element-247" data-mp="6s7txv5cjs1r">
                    {confirmMpin.map((digit, index) => {
                  const isFilled = digit !== '';
                  const isError = confirmMpinComplete && mpinComplete && !mpinsMatch;
                  const isSuccess = confirmMpinComplete && mpinComplete && mpinsMatch;
                  return <motion.div key={index} animate={isError ? {
                    x: [0, -4, 4, -4, 4, 0]
                  } : isFilled ? {
                    scale: [1, 1.08, 1]
                  } : {
                    scale: 1,
                    x: 0
                  }} transition={{
                    duration: isError ? 0.4 : 0.2
                  }} className="relative" data-id="element-248" data-mp="1ydamstnlf37" data-mpi={index}>
                          <input id={`cmpin-${index}`} type="text" inputMode="numeric" autoComplete="off" maxLength={1} value={digit ? '•' : ''} onChange={e => handleMpinDigitChange(index, e.target.value, true)} onKeyDown={e => handleMpinKeyDown(index, e, true)} onFocus={e => e.target.select()} className={`text-center text-[22px] font-bold outline-none border transition-all duration-200 caret-transparent ${isError ? 'border-red-500 text-red-500' : isSuccess ? 'border-[#F37024] text-[#F37024]' : isFilled ? 'border-[#F37024] text-[#F37024]' : `${inputBorderStyle} ${textStyle} focus:border-[#F37024]`}`} style={{
                      width: 50,
                      height: 60,
                      borderRadius: 8,
                      background: '#FFFFFF'
                    }} data-id="element-249" data-mp="2a60dwgc0kxlz" />
                        </motion.div>;
                })}
                  </div>

                  {confirmMpinComplete && mpinComplete && !mpinsMatch && <motion.p initial={{
                opacity: 0,
                y: -4
              }} animate={{
                opacity: 1,
                y: 0
              }} className="text-[12px] text-red-500 font-medium text-center mt-3" data-id="element-250" data-mp="3oout5ejyud89">
                      PINs don't match — please re-enter
                    </motion.p>}
                </div>
              </div>

              <div className="mt-auto pb-8 pt-6 flex justify-center" data-id="element-251" data-mp="30x5yhrjo9co5">
                <RegistrationCTAButton onClick={() => setShowLinkingModal(true)} disabled={!mpinValid} data-id="element-252" data-mp="35z4xyqcajpft">
                  Continue
                </RegistrationCTAButton>
              </div>
            </motion.div>}

          {/* 9. BVS Verification */}
          {step === 'bvs' && <motion.div key="bvs" initial={{
          opacity: 0,
          x: 20
        }} animate={{
          opacity: 1,
          x: 0
        }} exit={{
          opacity: 0,
          x: -20
        }} className="flex-1 flex flex-col -mx-6" data-id="element-253" data-mp="2i9kg6arviy84">
              <AnimatePresence mode="wait" data-id="element-254" data-mp="14an4satf8dyp">
                {(bvsPhase === 'right_guidance' || bvsPhase === 'left_guidance') && <motion.div key={`guidance-${bvsPhase}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="flex-1 flex flex-col px-6" data-id="element-255" data-mp="109ba3qvox2jn">
                    <div className="flex items-center justify-center relative mb-8" data-id="element-256" data-mp="lewzoeq6z1gl">
                      <h2 className={`text-lg font-bold ${textStyle}`} data-id="element-257" data-mp="gmwua6rcrgcm">
                        Guidance
                      </h2>
                    </div>
                    <div className="text-center mb-8" data-id="element-258" data-mp="cj79lsdstiwv">
                      <p className={`text-base leading-relaxed ${textStyle}`} data-id="element-259" data-mp="7nryh876e145">
                        Line up your hand with the guide
                        <br data-id="element-260" data-mp="2pi5n48xbclp" />
                        Keep your fingers together
                        <br data-id="element-261" data-mp="3tugs5bj1gful" />
                        Then stay still.
                      </p>
                    </div>

                    <div className="flex-1 relative flex items-center justify-center" data-id="element-262" data-mp="1mgnkv8aky8ku">
                      <motion.div animate={{
                  y: [-5, 5, -5]
                }} transition={{
                  duration: 4,
                  repeat: Infinity,
                  ease: 'easeInOut'
                }} className="relative w-full max-w-[280px] aspect-[3/4] border-4 border-white/20 rounded-[40px] bg-black/10 overflow-hidden flex items-center justify-center" data-id="element-263" data-mp="1qhzctkgxf5w7">
                        <div className={`w-3/4 h-1/2 opacity-50 ${bvsPhase === 'left_guidance' ? '-scale-x-100' : ''}`} data-id="element-264" data-mp="17miqos0br0aa">
                          <svg viewBox="0 0 100 100" className="w-full h-full fill-current text-[#F37024]" data-id="element-265" data-mp="trzu4x72aqzw">
                            <path d="M20,80 C20,50 40,30 40,30 L45,10 C45,5 55,5 55,10 L50,30 L60,15 C60,10 70,10 70,15 L60,35 L75,20 C75,15 85,15 85,20 L70,40 L85,30 C85,25 95,25 95,30 L75,60 C70,80 50,90 50,90 L20,80 Z" data-id="element-266" data-mp="jvmznx3p0943" />
                          </svg>
                        </div>
                      </motion.div>
                    </div>

                    <div className="mt-8 pb-8" data-id="element-267" data-mp="18nygusolron">
                      <h3 className={`text-center font-bold text-xl mb-6 uppercase tracking-widest ${textStyle}`} data-id="element-268" data-mp="f7440iet5c57">
                        Scan your{' '}
                        {bvsPhase === 'right_guidance' ? 'RIGHT' : 'LEFT'} hand
                      </h3>
                      <div className="flex justify-center" data-id="element-269" data-mp="o6943t60pdjh">
                        <RegistrationCTAButton onClick={() => {
                    setBvsCameraTimer(3);
                    setBvsPhase(bvsPhase === 'right_guidance' ? 'right_camera' : 'left_camera');
                  }} data-id="element-270" data-mp="k6qiswaab0g4">
                          Continue
                        </RegistrationCTAButton>
                      </div>
                    </div>
                  </motion.div>}

                {(bvsPhase === 'right_camera' || bvsPhase === 'left_camera') && <motion.div key={`camera-${bvsPhase}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="flex-1 flex flex-col bg-black relative" data-id="element-271" data-mp="12ulp5v3khbz7">
                    <div className="absolute inset-0 overflow-hidden" data-id="element-272" data-mp="1v9nnfd0mw1sx">
                      <img src="https://images.unsplash.com/photo-1559757175-5700dde675bc?auto=format&fit=crop&w=800&q=80" alt="camera feed" className="w-full h-full object-cover opacity-60" data-id="element-273" data-mp="1qbvtyekcc7mm" />
                      <div className="absolute inset-8 border-4 border-white rounded-[60px] opacity-80" data-id="element-274" data-mp="1glsqfkflfba1" />
                      <motion.div animate={{
                  top: ['10%', '90%', '10%']
                }} transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'linear'
                }} className="absolute left-8 right-8 h-1 bg-green-500 shadow-[0_0_15px_rgba(34,197,94,1)]" data-id="element-275" data-mp="1bmg5544dec9k" />
                    </div>

                    <div className="relative z-10 p-6 bg-gradient-to-b from-black/80 to-transparent" data-id="element-276" data-mp="xnq81ohjwcl7">
                      <div className="flex items-center justify-between mb-4" data-id="element-277" data-mp="39cebhy6izbxo">
                        <button onClick={() => setBvsPhase(bvsPhase === 'right_camera' ? 'right_guidance' : 'left_guidance')} className="text-white font-bold text-sm" data-id="element-278" data-mp="3e7tmmid5etqe">
                          Cancel
                        </button>
                        <span className="text-white font-bold text-sm" data-id="element-279" data-mp="3n7qgqzib4ksu">
                          4 Fingers TouchlessID
                        </span>
                        <div className="w-3 h-3 rounded-full bg-red-500 animate-pulse" data-id="element-280" data-mp="2vjyd5aokk233" />
                      </div>
                      <div className="text-center text-white" data-id="element-281" data-mp="2zixjmzpy86wn">
                        <p className="font-bold mb-1" data-id="element-282" data-mp="34bguvq15ypya">
                          Place your{' '}
                          {bvsPhase === 'right_camera' ? 'right' : 'left'} hand
                          in the box and stay still
                        </p>
                        <p className="text-sm opacity-80" data-id="element-283" data-mp="2bsthkwsesf7r">
                          Move hand further away
                        </p>
                      </div>
                    </div>
                  </motion.div>}

                {(bvsPhase === 'right_success' || bvsPhase === 'left_success') && <motion.div key={`success-${bvsPhase}`} initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} exit={{
              opacity: 0
            }} className="absolute inset-0 z-50 flex items-center justify-center p-6 bg-black/80 backdrop-blur-sm" data-id="element-284" data-mp="3outr7pzrohz3">
                    <motion.div initial={{
                scale: 0.9,
                y: 20
              }} animate={{
                scale: 1,
                y: 0
              }} className={`w-full max-w-sm rounded-[32px] p-8 flex flex-col items-center text-center shadow-2xl ${isPlatinum ? 'bg-[#2A2A2C]' : isDark ? 'bg-[#2D1810]' : 'bg-white'}`} data-id="element-285" data-mp="2r9z6mkh4nfck">
                      <motion.div initial={{
                  scale: 0
                }} animate={{
                  scale: 1
                }} transition={{
                  type: 'spring',
                  damping: 12,
                  stiffness: 200
                }} className="w-24 h-24 rounded-full bg-[#22C55E]/15 flex items-center justify-center mb-6 border-4 border-[#22C55E]/30" data-id="element-286" data-mp="gerxfi5ehz6b">
                        <CheckCircle2Icon className="w-12 h-12 text-[#22C55E]" strokeWidth={2} data-id="element-287" data-mp="j1ariq9xf6b0" />
                      </motion.div>
                      <h2 className={`text-2xl font-bold mb-3 ${textStyle}`} data-id="element-288" data-mp="16qlvshqhjitw">
                        {bvsPhase === 'right_success' ? 'Right Hand Scanned' : 'Left Hand Scanned'}
                      </h2>
                      <p className={`text-sm leading-relaxed mb-8 ${subtextStyle}`} data-id="element-289" data-mp="wzr06mrcn6vs">
                        {bvsPhase === 'right_success' ? 'Great job! Your right hand was scanned successfully.' : 'All biometrics captured successfully.'}
                      </p>
                      <div className="flex justify-center" data-id="element-290" data-mp="1ugg6otr8uxbi">
                        <RegistrationCTAButton onClick={() => {
                    if (bvsPhase === 'right_success') setBvsPhase('left_guidance');
                    if (bvsPhase === 'left_success') onComplete(userType as 'bop_account' | 'bop_credit_card');
                  }} data-id="element-291" data-mp="6r39xsnauw04">
                          Continue
                        </RegistrationCTAButton>
                      </div>
                    </motion.div>
                  </motion.div>}
              </AnimatePresence>
            </motion.div>}

          {/* In-flow cooling-off step removed — handled by dedicated CoolingOffScreen at the app level */}
        </AnimatePresence>
      </div>

      {/* ── Mobile Device Linking bottom-sheet modal — shown after MPIN is confirmed ── */}
      <AnimatePresence data-id="element-292" data-mp="arwz0pchagul">
        {showLinkingModal && <>
            {/* Backdrop */}
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} className="fixed inset-0 z-[100]" style={{
          background: 'rgba(0,0,0,0.5)'
        }} data-id="element-293" data-mp="1893zfmbkc9f2" />

            {/* Bottom sheet */}
            <motion.div initial={{
          y: '100%'
        }} animate={{
          y: 0
        }} exit={{
          y: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed bottom-0 left-0 right-0 z-[101] max-w-[430px] mx-auto" style={{
          background: '#FFFFFF',
          borderTopLeftRadius: 28,
          borderTopRightRadius: 28,
          boxShadow: '0 -10px 40px rgba(0,0,0,0.18)',
          padding: '40px 28px 32px'
        }} data-id="element-294" data-mp="1fr1w2q8nbq0v">
              {/* Link / chain icon */}
              <div className="flex justify-center mb-6" data-id="element-295" data-mp="23jig36by1s2t">
                <svg width="56" height="56" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-296" data-mp="28exr7qikh9vj">
                  <path d="M9.5 14.5L14.5 9.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" data-id="element-297" data-mp="2dgwrgrj0fqo9" />
                  <path d="M11 6.5L13 4.5C14.6569 2.84315 17.3431 2.84315 19 4.5C20.6569 6.15685 20.6569 8.84315 19 10.5L17 12.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-298" data-mp="2ieok5nrhbgti" />
                  <path d="M13 17.5L11 19.5C9.34315 21.1569 6.65685 21.1569 5 19.5C3.34315 17.8431 3.34315 15.1569 5 13.5L7 11.5" stroke="#9E9E9E" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" data-id="element-299" data-mp="1np3n386dgc23" />
                </svg>
              </div>

              {/* Title */}
              <h3 style={{
            fontSize: 18,
            fontWeight: 700,
            color: '#1A1A1A',
            textAlign: 'center',
            marginBottom: 14
          }} data-id="element-300" data-mp="2bexvndjnregj">
                Mobile Device Linking
              </h3>

              {/* Description */}
              <p style={{
            fontSize: 14,
            color: '#5A5A5A',
            textAlign: 'center',
            lineHeight: 1.6,
            marginBottom: 32,
            padding: '0 6px'
          }} data-id="element-301" data-mp="2gh6sikznj0nx">
                Your device is being linked to the digiBOP Mobile Banking App. Please set up biometric for seamless access in the future.
              </p>

              {/* Proceed button */}
              <button onClick={() => {
            setShowLinkingModal(false);
            if (accountNumber.startsWith('0000')) {
              // RDA users skip BVS — go straight to the app-level cooling-off screen
              onComplete(userType as 'bop_account' | 'bop_credit_card');
            } else {
              handleNext('bvs');
            }
          }} className="w-full active:scale-[0.98] transition-transform" style={{
            height: 56,
            borderRadius: 14,
            background: '#5A1408',
            color: '#FFFFFF',
            fontSize: 16,
            fontWeight: 600,
            border: 'none',
            cursor: 'pointer'
          }} data-id="element-302" data-mp="32nrdgg4cajd3">
                Proceed
              </button>
            </motion.div>
          </>}
      </AnimatePresence>
    </div>;
}